import React, { useEffect, useContext } from "react";
import { observer } from "mobx-react";
import PaginationComponent from "Components/common/PaginationComponent";
import OutboxCardList from "../OutboxCardList";
import { outboxStoreData } from "../outboxStore";
import { OutboxContext } from "../main";
import inboxV2Store from "./inboxV2Store";
import {
  ContactRailSkeleton,
  ConversationsPaneSkeleton,
  ThreadPaneSkeleton
} from "./InboxV2Loader";
import ContactRail from "./ContactRail";
import ConversationsPane from "./ConversationsPane";
import EmailThread from "./EmailThread";
import LinkedInThread from "./LinkedInThread";

/*
 * Inbox V2 (SG-2158), rendered only when inboxV2Enabled is on AND the Replied tab is
 * active -- that is the only combination where the backend collapses the listing to one
 * row per contact. With no contact selected this is the existing full-width list; the
 * rows are already contact-grouped server side, so it is reused as-is.
 */
const InboxV2 = observer((props = {}) => {
  const { getOutboxData = () => {} } = props || {};
  const { inputValues = {}, getReloadOutboxCardData = () => {} } =
    useContext(OutboxContext);
  const { dataLoading = false, outboxData = {} } = inputValues || {};
  const { result = [], totalPages = 0, currentPage = 0 } = outboxData || {};
  const { selectedContact = null, feed = [], openConversationId = "" } =
    inboxV2Store || {};

  // Same reload path as OutboxCardList's paginator; InboxV2's effect re-syncs the open
  // contact after the new page loads.
  const paginate = (pageNoObj = {}) => {
    if (dataLoading) return;
    outboxStoreData.setActiveOutboxId("");
    outboxStoreData.setCurrentPage(
      pageNoObj?.selected ? pageNoObj.selected + 1 : 1
    );
    getReloadOutboxCardData();
  };

  useEffect(() => {
    return () => inboxV2Store.reset();
  }, []);

  // A filter/page change reloads the list. Stay in the 3-pane view: if the open contact
  // is gone but the (filtered) list still has rows, open the first one; only fall back to
  // the full list when the filter yields nothing.
  useEffect(() => {
    const selectedId = inboxV2Store.getContactId(
      inboxV2Store?.selectedContact || {}
    );
    if (!selectedId || dataLoading) return;
    const stillListed = result.some(item => item?.contact?.[0]?.id === selectedId);
    if (stillListed) return;
    if (result.length > 0) {
      inboxV2Store.selectContact(result[0]);
    } else {
      inboxV2Store.clearSelection();
    }
  }, [result, dataLoading]);

  if (!selectedContact) {
    return (
      <OutboxCardList
        getOutboxData={getOutboxData}
        onRowClick={item => inboxV2Store.selectContact(item)}
      />
    );
  }

  const openConversation =
    feed?.find(item => item?.id === openConversationId) || null;

  // Keep the pagination mounted through every loading state (it belongs to the contact
  // list, not the conversations). A list reload skeletons all three panes; selecting a
  // contact skeletons only the right two and keeps the rail clickable.
  const panes = dataLoading ? (
    <>
      <ContactRailSkeleton />
      <ConversationsPaneSkeleton />
      <ThreadPaneSkeleton />
    </>
  ) : inboxV2Store?.conversationsLoading ? (
    <>
      <ContactRail />
      <ConversationsPaneSkeleton />
      <ThreadPaneSkeleton />
    </>
  ) : (
    <>
      <ContactRail />
      <ConversationsPane />
      {openConversation ? (
        // Key by conversation id so switching conversations remounts the thread -- that
        // runs EmailThread's cleanup, which closes an open inline reply editor.
        openConversation?.type === "linkedin" ? (
          <LinkedInThread
            key={openConversation.id}
            conversation={openConversation}
          />
        ) : (
          <EmailThread
            key={openConversation.id}
            conversation={openConversation}
          />
        )
      ) : (
        <div className="inboxV2ThreadPane">
          <div className="inboxV2EmptyBar">
            <button
              type="button"
              className="inboxV2Close"
              onClick={() => inboxV2Store.clearSelection()}
            >
              <i className="material-icons">close</i>
            </button>
          </div>
          <div className="inboxV2EmptyBody">
            No conversations found for this contact
          </div>
        </div>
      )}
    </>
  );

  return (
    <div className="inboxV2">
      <div className="inboxV2Panes">{panes}</div>

      {(dataLoading || totalPages > 1) && (
        <div className="inboxV2Pagination">
          <PaginationComponent
            callback={paginate}
            loading={dataLoading}
            totalPages={totalPages}
            currentPage={currentPage}
          />
        </div>
      )}
    </div>
  );
});

export default InboxV2;
